import React from 'react';
import {Alert, Collapse, Grid, TextField} from "@mui/material";
import {customNumberField} from '../util/BookingUtil';
import {styled} from "@mui/material/styles";

type DisplayOverrideContainerAdditionalProps = {
  displayOverrideWarning?: boolean;
}

const DisplayOverrideContainer = styled(Grid, {
  shouldForwardProp: (propName) => propName !== 'displayOverrideWarning'
})(({displayOverrideWarning = false}: DisplayOverrideContainerAdditionalProps) => (displayOverrideWarning && {
  marginTop: '1.2em',
}));

interface CollapsibleMinSpaceOverrideAlertProps {
  in: boolean;
  minimumSpacesRequired: number;
}

export const CollapsibleMinSpaceOverrideAlert: React.FC<CollapsibleMinSpaceOverrideAlertProps> = (props) => {
  const {in: show, minimumSpacesRequired} = props;

  return <Collapse in={show} className={"w-100"}>
    <Alert severity={"info"}>
      {`This location requires at least ${minimumSpacesRequired} ${minimumSpacesRequired === 1 ? "space" : "spaces"} to book. You are overriding this minimum.`}
    </Alert>
  </Collapse>;
};

interface BookingNumberOfSpacesTextFieldProps {
  value: any;
  onChange: (value: any) => void;
  minimumSpacesRequired?: number;
  overrideMinimum?: boolean;
  prePendedContent?: any;
  postPendedContent?: any;
  hideOverrideNotice?: boolean;

  [key: string]: any;
}

const MAX_VALUE = 9999;

const BookingNumberOfSpacesTextField: React.FC<BookingNumberOfSpacesTextFieldProps> = ({
                                                                                         value,
                                                                                         onChange,
                                                                                         minimumSpacesRequired = 1,
                                                                                         overrideMinimum = false,
                                                                                         prePendedContent,
                                                                                         postPendedContent,
                                                                                         hideOverrideNotice = false,
                                                                                         ...otherProps
                                                                                       }) => {
  const intValue = Number.parseInt(value, 10);
  const isValid = overrideMinimum ? intValue >= 0 : intValue >= minimumSpacesRequired;
  let errorMsg = '';
  if (!isValid) {
    errorMsg = overrideMinimum ?
      'A booking requires at least 0 spaces to process.' :
      `This location requires a minimum of ${minimumSpacesRequired} ${minimumSpacesRequired > 1 ? 'spaces' : 'space'} to book.`;
  }

  const displayOverrideWarning = overrideMinimum && intValue < minimumSpacesRequired;

  const handleSpaceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = customNumberField(event.target.value);
    if (value === '') {
      onChange(value);
    } else {
      const intValue = Number.parseInt(value);
      if (intValue >= 1 && intValue <= MAX_VALUE) {
        onChange(intValue);
      }
    }
  };

  return (
    <Grid container>
      <Grid item container xs={12} wrap={'nowrap'} alignItems={'center'}>
        {prePendedContent && <Grid item>{prePendedContent}</Grid>}
        <TextField
          value={value}
          onChange={handleSpaceChange}
          error={!isValid}
          helperText={errorMsg}
          variant='standard'
          type={'number'}
          inputProps={{
            inputMode: 'numeric',
            htmlInput: {
              min: 1,
              max: MAX_VALUE
            }
          }}
          onWheel={(e) => (e.target as HTMLElement).blur()}
          {...otherProps}
        />
        {postPendedContent && <Grid item>{postPendedContent}</Grid>}
      </Grid>
      {
        !hideOverrideNotice && <DisplayOverrideContainer item xs={12} displayOverrideWarning={displayOverrideWarning}>
          <CollapsibleMinSpaceOverrideAlert in={displayOverrideWarning} minimumSpacesRequired={minimumSpacesRequired}/>
        </DisplayOverrideContainer>
      }
    </Grid>
  );
};

export default BookingNumberOfSpacesTextField;
